import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Check, Sparkle, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const SLOTS = [
  { key: "hoodies", label: "Hoodie" },
  { key: "tees", label: "Tee" },
  { key: "dog-gear", label: "Dog Gear" },
];

export default function BuildAFit() {
  const { addItem } = useCart();
  const [all, setAll] = useState([]);
  const [picks, setPicks] = useState({});

  useEffect(() => { api.get("/products").then(({ data }) => setAll(data)).catch(() => {}); }, []);

  const byCat = useMemo(() => {
    const m = {};
    for (const s of SLOTS) m[s.key] = all.filter((p) => p.category === s.key);
    return m;
  }, [all]);

  const selected = SLOTS.map((s) => picks[s.key]).filter(Boolean);
  const total = selected.reduce((sum, p) => sum + p.price, 0);

  const pick = (cat, product) =>
    setPicks((prev) => ({ ...prev, [cat]: prev[cat]?.id === product.id ? undefined : product }));

  const addFit = () => {
    if (selected.length === 0) { toast.error("Pick at least one piece first"); return; }
    selected.forEach((p) => addItem(p));
    toast.success("Full fit added to your bag 🐾");
    setPicks({});
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="build-a-fit-page">
      <div className="flex items-center gap-3 mb-2">
        <Sparkle weight="fill" size={22} className="text-primary" />
        <span className="overline text-primary">P'IT FACTOR™ · ShopKing</span>
      </div>
      <h1 className="text-4xl sm:text-5xl tracking-tighter font-light mb-3">Build‑A‑Fit</h1>
      <p className="text-muted-foreground max-w-xl mb-10">Stack your hoodie, tee and dog gear, watch the total move live, and drop the whole fit — for you <span className="text-white">and</span> your pit — in one tap.</p>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Picker */}
        <div className="lg:col-span-2 space-y-10">
          {SLOTS.map((s) => (
            <div key={s.key} data-testid={`fit-slot-${s.key}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-modern text-xl font-medium">{s.label}</h2>
                {picks[s.key] && <span className="overline text-primary">{picks[s.key].name}</span>}
              </div>
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                {(byCat[s.key] || []).map((p) => {
                  const active = picks[s.key]?.id === p.id;
                  return (
                    <button key={p.id} onClick={() => pick(s.key, p)}
                      className={`shrink-0 w-40 text-left rounded-xl overflow-hidden border transition-colors ${active ? "border-primary neon-glow" : "border-white/10 hover:border-white/25"}`}
                      data-testid={`fit-option-${p.id}`}>
                      <div className="relative aspect-[4/5] bg-muted">
                        <img src={(p.images || [])[0]} alt={p.name} className="h-full w-full object-cover" />
                        {active && <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center"><Check weight="bold" size={14} /></span>}
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-modern leading-tight line-clamp-2">{p.name}</p>
                        <p className="text-sm text-primary mt-1">${p.price}</p>
                      </div>
                    </button>
                  );
                })}
                {(byCat[s.key] || []).length === 0 && <p className="text-sm text-muted-foreground py-8">Dropping soon.</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Live preview */}
        <div className="h-fit lg:sticky lg:top-24 p-6 rounded-2xl bg-card border border-primary/20 neon-glow" data-testid="fit-preview">
          <p className="overline text-primary mb-4">Your fit</p>
          {selected.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Pick pieces to build your fit.</p>
          ) : (
            <div className="space-y-3 mb-5">
              {selected.map((p) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`fit-selected-${p.id}`}>
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted shrink-0"><img src={(p.images || [])[0]} alt="" className="h-full w-full object-cover" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-modern truncate">{p.name}</p></div>
                  <span className="text-sm text-primary">${p.price}</span>
                </div>
              ))}
            </div>
          )}
          <div className="h-px bg-white/10 my-4" />
          <div className="flex justify-between items-center mb-5">
            <span className="overline text-muted-foreground">Running total</span>
            <motion.span key={total} initial={{ scale: 1.2, color: "#ff1493" }} animate={{ scale: 1 }} className="text-2xl font-modern text-primary" data-testid="fit-total">${total.toLocaleString()}</motion.span>
          </div>
          <button onClick={addFit} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="fit-add">
            <ShoppingBag weight="fill" size={18} /> Add full fit to bag
          </button>
        </div>
      </div>
    </div>
  );
}
